import { useEffect, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/services/api';
import { futebolApi, STATUS_DA_TELA, type TipoImagem } from '@/services/futebol';
import { useAuth } from '@/store/useAuth';
import type { SerieCbf } from '@/types/futebol';

export const futebolKeys = {
  all: ['futebol'] as const,
  acesso: ['futebol', 'acesso'] as const,
  serie: (s: SerieCbf) => ['futebol', 'serie', s] as const,
  rodadas: (s: SerieCbf, fase: string | null) => ['futebol', 'rodadas', s, fase] as const,
  rodada: (s: SerieCbf, n: number, fase: string | null) =>
    ['futebol', 'rodada', s, n, fase] as const,
  jogo: (id: number) => ['futebol', 'jogo', id] as const,
  clube: (id: number) => ['futebol', 'clube', id] as const,
  busca: (q: string) => ['futebol', 'busca', q] as const,
  atleta: (id: number) => ['futebol', 'atleta', id] as const,
  imagem: (tipo: TipoImagem, id: number) => ['futebol', 'imagem', tipo, id] as const,
  estado: ['futebol', 'estado'] as const,
};

/** Os dados se renovam sozinhos a cada minuto, enquanto o bloco está na tela. */
const RENOVAR_MS = 60_000;

/** 403, 404 e 503 são respostas do servidor que a tela explica: repetir não muda nada. */
function repetir(vezes: number, erro: unknown): boolean {
  if (erro instanceof ApiError && (STATUS_DA_TELA as readonly number[]).includes(erro.status)) {
    return false;
  }
  return vezes < 1;
}

const LEITURA = {
  staleTime: 30_000,
  refetchInterval: RENOVAR_MS,
  retry: repetir,
} as const;

/**
 * Sonda do menu: o item "Futebol Brasileiro" só aparece se a resposta disser
 * `habilitado`. Qualquer falha (sem sessão, 4xx, rede) conta como "não habilitado".
 */
export function useFutebolAcesso() {
  const username = useAuth((s) => s.username);
  return useQuery({
    queryKey: futebolKeys.acesso,
    queryFn: futebolApi.acesso,
    enabled: !!username,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useClassificacao(serie: SerieCbf) {
  return useQuery({
    queryKey: futebolKeys.serie(serie),
    queryFn: () => futebolApi.serie(serie),
    placeholderData: keepPreviousData,
    ...LEITURA,
  });
}

export function useRodadas(serie: SerieCbf, fase: string | null) {
  return useQuery({
    queryKey: futebolKeys.rodadas(serie, fase),
    queryFn: () => futebolApi.rodadas(serie, fase),
    ...LEITURA,
  });
}

export function useRodada(serie: SerieCbf, n: number | null, fase: string | null) {
  return useQuery({
    queryKey: futebolKeys.rodada(serie, n ?? 0, fase),
    queryFn: () => futebolApi.rodada(serie, n as number, fase),
    enabled: n !== null,
    placeholderData: keepPreviousData,
    ...LEITURA,
  });
}

export function useJogo(id: number | null) {
  return useQuery({
    queryKey: futebolKeys.jogo(id ?? 0),
    queryFn: () => futebolApi.jogo(id as number),
    enabled: id !== null,
    ...LEITURA,
  });
}

export function useClube(id: number | null) {
  return useQuery({
    queryKey: futebolKeys.clube(id ?? 0),
    queryFn: () => futebolApi.clube(id as number),
    enabled: id !== null,
    ...LEITURA,
  });
}

/** Busca por nome: só dispara com 2 letras ou mais. O adiamento (300 ms) é de quem digita. */
export function useBuscaAtletas(q: string) {
  const termo = q.trim();
  return useQuery({
    queryKey: futebolKeys.busca(termo),
    queryFn: () => futebolApi.buscarAtletas(termo),
    enabled: termo.length >= 2,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    retry: repetir,
  });
}

export function useAtleta(id: number | null) {
  return useQuery({
    queryKey: futebolKeys.atleta(id ?? 0),
    queryFn: () => futebolApi.atleta(id as number),
    enabled: id !== null,
    ...LEITURA,
  });
}

export function useEstadoColeta(ativo: boolean) {
  return useQuery({
    queryKey: futebolKeys.estado,
    queryFn: futebolApi.estado,
    enabled: ativo,
    staleTime: 10_000,
    refetchInterval: 15_000,
    retry: repetir,
  });
}

export function useKillSwitch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ligado, motivo }: { ligado: boolean; motivo?: string }) =>
      futebolApi.killSwitch(ligado, motivo),
    onSuccess: () => qc.invalidateQueries({ queryKey: futebolKeys.estado }),
  });
}

export function useVarredura() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: futebolApi.varredura,
    onSuccess: () => qc.invalidateQueries({ queryKey: futebolKeys.estado }),
  });
}

/**
 * Escudo ou foto como endereço local (`blob:`). A imagem exige o JWT, então não
 * dá para usar `<img src="/api/...">`. O cache por id fica no react-query (nunca
 * rebaixa a mesma imagem ao trocar de aba); o endereço local é revogado ao sair.
 * Sem imagem coletada (404) ou fora do plano (403), devolve `null` e a tela
 * mostra as iniciais.
 */
export function useImagemFutebol(tipo: TipoImagem, id: number | null | undefined, ativo = true) {
  const consulta = useQuery({
    queryKey: futebolKeys.imagem(tipo, id ?? 0),
    queryFn: () => futebolApi.imagem(tipo, id as number),
    enabled: ativo && id !== null && id !== undefined,
    staleTime: Infinity,
    gcTime: 30 * 60_000,
    retry: false,
  });
  const blob = consulta.data;
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob || typeof URL.createObjectURL !== 'function') {
      setUrl(null);
      return;
    }
    const local = URL.createObjectURL(blob);
    setUrl(local);
    return () => URL.revokeObjectURL(local);
  }, [blob]);
  return url;
}
